import { Fragment } from "react";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { Icon } from "../Icons/Icon";

interface StepsProps {
  /** Zero-based index of the step being worked on. */
  current: number;
  total: number;
}

/**
 * Progress through a numbered sequence — the circles-and-rails bar at the top
 * of a wizard. Steps behind you are green and ticked, the one you're on is
 * brand, the rest are waiting.
 *
 * It shows position, not names. For a list of what each step *is*, use
 * `StepList`; for one step's heading on a long form, use `StepHeader`.
 */
export function Steps({ current, total }: StepsProps) {
  const steps = Array.from({ length: total }, (_, i) => i);

  return (
    <div className="flex w-full items-center">
      {steps.map((index) => {
        const done = index < current;
        const active = index === current;
        // The rail *into* a step is coloured by that step's state, so the trail
        // behind you reads as one continuous run rather than dashes.
        const railDone = index <= current;

        return (
          <Fragment key={index}>
            {index > 0 && (
              <div
                className={`mx-4 h-1 flex-1 ${railDone ? "bg-brand" : "bg-border"}`}
              />
            )}
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                done
                  ? "bg-green text-white"
                  : active
                    ? "bg-brand text-white"
                    : "bg-border text-content-muted"
              }`}
            >
              {done ? (
                <Icon icon={CheckIcon} weight="bold" size="lg" />
              ) : (
                index + 1
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
